import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { getBaseUrl } from "@/lib/authUrl";
import { fetchProfile, upsertProfile, upsertGamification, updateProfileAvatar, AUTH_USER_MISSING_ERROR_CODE, type Profile } from "@/lib/db";

/** Email that can enable hidden Admin mode (toggle in profile dropdown). */
export const ADMIN_EMAIL = "adabilbao777@gmail.com";

/** Resolve display avatar: profile override first, then provider (Google picture). */
export function getAvatarUrl(profile: Profile | null, user: User | null): string | null {
  if (profile?.avatar_url) return profile.avatar_url;
  const meta = user?.user_metadata;
  return (meta?.avatar_url ?? meta?.picture) ?? null;
}

const ADMIN_MODE_STORAGE_KEY = "ld_admin_mode";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: Profile | null;
  profileLoading: boolean;
  /** Avatar URL to display (profile override or provider picture). */
  avatarUrl: string | null;
  /** true when profile exists and is_teacher is not null */
  isProfileComplete: boolean;
  /** true when user has completed profile and is_teacher === true */
  isTeacher: boolean;
  /** true when current user email is ADMIN_EMAIL */
  isAdminUser: boolean;
  /** Toggled by admin user in profile dropdown; persisted in localStorage. Other parts of the app can check this for admin-only UI. */
  isAdminMode: boolean;
  setIsAdminMode: (value: boolean) => void;
  refreshProfile: () => Promise<void>;
  completeProfile: (data: { username: string; birthDate: string; isTeacher: boolean }) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getStoredAdminMode(): boolean {
  try {
    return localStorage.getItem(ADMIN_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdminMode, setIsAdminModeState] = useState<boolean>(getStoredAdminMode);

  const setIsAdminMode = useCallback((value: boolean) => {
    setIsAdminModeState(value);
    try {
      if (value) {
        localStorage.setItem(ADMIN_MODE_STORAGE_KEY, "true");
      } else {
        localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    setProfileLoading(true);
    try {
      const p = await fetchProfile(user.id);
      setProfile(p);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      refreshProfile();
      // Auto-default to Admin mode for ADMIN_EMAIL when no stored preference exists
      if (user.email === ADMIN_EMAIL) {
        try {
          if (localStorage.getItem(ADMIN_MODE_STORAGE_KEY) === null) {
            setIsAdminMode(true);
          }
        } catch {
          // ignore
        }
      }
    } else {
      setProfile(null);
      setIsAdminModeState(false);
      try {
        localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }, [user?.id, user?.email, refreshProfile, setIsAdminMode]);

  // On sign-in: if profile exists but has no avatar_url, sync from provider (e.g. Google picture)
  useEffect(() => {
    if (!user || !profile || profile.avatar_url) return;
    const providerUrl =
      (user.user_metadata?.avatar_url ?? user.user_metadata?.picture) as string | undefined;
    if (!providerUrl?.trim()) return;

    let cancelled = false;
    (async () => {
      try {
        await updateProfileAvatar(user.id, providerUrl);
        if (!cancelled) await refreshProfile();
      } catch (err) {
        console.warn("[Auth] Sync provider avatar failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.id, profile?.avatar_url, refreshProfile]);

  const avatarUrl = getAvatarUrl(profile, user);

  const completeProfile = useCallback(
    async (data: { username: string; birthDate: string; isTeacher: boolean }) => {
      if (!user) throw new Error("Not authenticated");
      if (typeof console.debug === "function") {
        console.debug("[complete_registration] complete_registration_started");
      }
      try {
        // 1) Update profiles table (username, birth_date, is_teacher) so onboarding is persisted
        await upsertProfile(user.id, {
          username: data.username,
          birth_date: data.birthDate,
          is_teacher: data.isTeacher,
        });
        // 2) Seed gamification with crystals: 0 for new accounts (force 0 at creation)
        await upsertGamification(user.id, { crystals: 0, xp: 0, currentStreak: 0 });
        // 3) Update local profile state immediately so ProtectedRoute sees isProfileComplete and hides the modal
        setProfile((prev) => ({
          id: user.id,
          username: data.username,
          birth_date: data.birthDate,
          is_teacher: data.isTeacher,
          avatar_url: prev?.avatar_url ?? null,
        }));
        // 4) Re-fetch from DB to stay in sync (e.g. created_at, server defaults)
        await refreshProfile();
        if (typeof console.debug === "function") {
          console.debug("[complete_registration] complete_registration_succeeded");
        }
      } catch (err: unknown) {
        const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : undefined;
        if (code === AUTH_USER_MISSING_ERROR_CODE) {
          if (typeof console.warn === "function") {
            console.warn("[complete_registration] complete_registration_recovery_failed", { reason: "auth_user_missing" });
          }
          await signOut();
          throw err;
        }
        throw err;
      }
    },
    [user?.id, refreshProfile]
  );

  /** Profile is complete only when all required fields are present and non-null. */
  const isProfileComplete =
    profile !== null &&
    profile.is_teacher !== null &&
    profile.username != null &&
    profile.username.trim() !== "" &&
    profile.birth_date != null;
  const isTeacher = isProfileComplete && profile!.is_teacher === true;
  const isAdminUser = user?.email === ADMIN_EMAIL;

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUpWithEmail = async (email: string, password: string) => {
    const redirectUrl = `${getBaseUrl()}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const redirectUrl = `${getBaseUrl()}/`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        profile,
        profileLoading,
        avatarUrl,
        isProfileComplete,
        isTeacher,
        isAdminUser,
        isAdminMode,
        setIsAdminMode,
        refreshProfile,
        completeProfile,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
